import { format } from "date-fns";

/**
 * Booking days (Reservation.dates, lastDate, proposedDates) are calendar days
 * of the dive center: a dive booked for "Oct 14" happens on Oct 14 in the
 * center's town, and that is the day everybody sees, wherever they are.
 *
 * Canonical forms:
 * - on the wire (API payloads, URLs): a "YYYY-MM-DD" day key;
 * - in the database: 00:00:00.000Z of that day (Postgres `date` columns,
 *   which Prisma returns as that instant).
 *
 * Rows written before the switch to calendar days hold other instants: the
 * diver's local midnight serialized to UTC (web) or 12:00Z (mobile webhook,
 * admin moves). toDayKey() maps all of them to the day the diver picked, so
 * the same code reads old and new rows correctly.
 */
export type DayKey = string;

const DAY_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

export function isDayKey(value: unknown): value is DayKey {
  if (typeof value !== "string" || !DAY_KEY_RE.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(value);
}

/**
 * The browser sends the picked calendar day as local midnight serialized to
 * UTC (e.g. a user in UTC+2 picking July 30 sends July 29 22:00Z). Normalizing
 * with server-local setHours() therefore lands on the WRONG day for any user
 * east of UTC. Shifting +12h and reading the UTC date recovers the day the
 * user actually picked for every timezone from UTC-11 to UTC+12.
 */
export function toIntendedUtcDay(date: Date): Date {
  const shifted = new Date(date.getTime() + 12 * 60 * 60 * 1000);
  return new Date(
    Date.UTC(
      shifted.getUTCFullYear(),
      shifted.getUTCMonth(),
      shifted.getUTCDate(),
    ),
  );
}

/** Prisma availability rules use Monday=1 through Sunday=7. */
export function isoWeekday(day: Date): number {
  return day.getUTCDay() || 7;
}

function utcDayKey(date: Date): DayKey {
  return date.toISOString().slice(0, 10);
}

/**
 * Day key of a stored or serialized booking day. Accepts a "YYYY-MM-DD" key,
 * a canonical 00:00Z value, and legacy instants: exactly 12:00Z is that UTC
 * day, anything else is a browser's local midnight and goes through
 * toIntendedUtcDay. Same rule as the calendar-day migration.
 */
export function toDayKey(value: Date | string): DayKey {
  if (typeof value === "string" && isDayKey(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new Error(`Invalid booking day: ${String(value)}`);
  }
  const isNoonUtc =
    date.getUTCHours() === 12 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0;
  return utcDayKey(isNoonUtc ? date : toIntendedUtcDay(date));
}

/** Day key of a Date built by a calendar widget (local midnight of the picked day). */
export function dayKeyFromLocalDate(date: Date): DayKey {
  return format(date, "yyyy-MM-dd");
}

/** Local-midnight Date for a day key, for calendar widgets and date-fns format(). */
export function localDateFromDayKey(value: Date | string): Date {
  const [year, month, day] = toDayKey(value).split("-").map(Number);
  return new Date(year, month - 1, day);
}

/** Value to store in a booking day column: 00:00:00.000Z of the day. */
export function dayKeyToDbDate(value: Date | string): Date {
  return new Date(`${toDayKey(value)}T00:00:00.000Z`);
}

/** Normalizes booking days from any accepted form to stored values. */
export function toDbBookingDays(values: Array<Date | string>): Date[] {
  return values.map(dayKeyToDbDate);
}

export function addDaysToDayKey(value: Date | string, days: number): DayKey {
  return utcDayKey(new Date(dayKeyToDbDate(value).getTime() + days * DAY_MS));
}

/** Whole days from `from` to `to` (both booking days). */
export function daysBetweenDayKeys(
  from: Date | string,
  to: Date | string,
): number {
  return Math.round(
    (dayKeyToDbDate(to).getTime() - dayKeyToDbDate(from).getTime()) / DAY_MS,
  );
}

/** Today's UTC calendar day. The dive center's timezone is not known yet. */
export function todayUtcDayKey(now: Date = new Date()): DayKey {
  return utcDayKey(now);
}

/**
 * Server-side cutoff for "upcoming" bookings: lastDate >= this value. It is
 * yesterday (UTC), so a booking stays upcoming until its day is over in every
 * timezone, and never becomes past during its own day.
 */
export function firstUpcomingBookingDay(now: Date = new Date()): Date {
  return dayKeyToDbDate(addDaysToDayKey(todayUtcDayKey(now), -1));
}

/**
 * True once the booking day is over for the viewer. Meant for the browser:
 * dive center staff look at bookings from the center's own timezone, so the
 * dive day itself is never "past".
 */
export function isBookingDayBeforeToday(value: Date | string): boolean {
  return toDayKey(value) < dayKeyFromLocalDate(new Date());
}

/**
 * Formats a booking day with a date-fns pattern, showing the same calendar
 * day in every browser and server timezone.
 */
export function formatBookingDay(date: Date | string, pattern: string): string {
  return format(localDateFromDayKey(date), pattern);
}
